import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useWavesurfer } from '@wavesurfer/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPause, faPlay } from '@fortawesome/free-solid-svg-icons';
import { useAudioContext } from './AudioContext';
import { useDispatch } from 'react-redux';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { registerAudioPlayer, unregisterAudioPlayer } from './UI/AudioManager';

interface AudioMessageProps {
  url: string;
  id: string;
}

const AudioPlayer: React.FC<AudioMessageProps> = ({ url, id }) => {
  const dispatch: ThunkDispatch<object, object, any> = useDispatch();
  const { playingAudioId, setPlayingAudioId, triggerPlayId, setTriggerPlayId } = useAudioContext();
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef(null);
  const { wavesurfer } = useWavesurfer({
    container: containerRef,
    height: 26,
    barWidth: 3,
    barRadius: 50,
    cursorWidth: 1,
    backend: 'WebAudio',
    autoCenter: true,
    progressColor: '#4658a9',
    waveColor: '#bababa',
    cursorColor: 'transparent',
  });

  const loadAudio = useCallback(() => {
    if (wavesurfer) {
      setIsLoading(true);
      wavesurfer.load(url);
      wavesurfer.on('ready', () => {
        setIsLoading(false);
        setAudioLoaded(true);
        wavesurfer.play();
        setIsPlaying(true);
        setPlayingAudioId(id);
      });
    }
  }, [wavesurfer, url, id, setPlayingAudioId]);

  const onPlayPause = useCallback(() => {
    if (wavesurfer) {
      wavesurfer.playPause();
      setIsPlaying(!isPlaying);
      if (!isPlaying) {
        setPlayingAudioId(id);
      } else {
        setPlayingAudioId(null);
      }
    }
  }, [wavesurfer, isPlaying, id, setPlayingAudioId]);

  useEffect(() => {
    if (wavesurfer) {
      wavesurfer.on('finish', () => {
        setIsPlaying(false);
        setPlayingAudioId(null);
      });
    }
  }, [wavesurfer, setPlayingAudioId]);

  useEffect(() => {
    if (playingAudioId && playingAudioId !== id && isPlaying) {
      wavesurfer && wavesurfer.pause();
      setIsPlaying(false);
      setPlayingAudioId(null);
    }
  }, [playingAudioId, id, isPlaying, wavesurfer, setPlayingAudioId]);

  useEffect(() => {
    if (triggerPlayId === id) {
      if (!audioLoaded) {
        loadAudio();
      } else {
        onPlayPause();
      }
      setTriggerPlayId(null);
    }
  }, [triggerPlayId]);

  useEffect(() => {
      if (wavesurfer) {
          registerAudioPlayer(id, wavesurfer);
      }
      return () => {
          unregisterAudioPlayer(id);
      };
  }, [wavesurfer, id]);
  
  return (
    <div className={`WaveformContainer`} key={id}>
      <button
        className="PlayButton"
        onClick={audioLoaded ? onPlayPause : loadAudio}
        disabled={isLoading}
      >
        <FontAwesomeIcon icon={!isPlaying ? faPlay : faPause} size="lg" />
      </button>
      {!audioLoaded && isLoading || !url ? (
        <div className="fake-wave"></div>
      ) : (
        <div ref={containerRef} className="Wave" />
      )}
    </div>
  );
};

export default AudioPlayer;